import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Eye, 
  Save, 
  Send, 
  BookOpen, 
  Users, 
  GraduationCap,
  FileText,
  Clock,
  User,
  Upload
} from 'lucide-react';
import { CoachingVideoPlayer } from './CoachingVideoPlayer';
import { IntelligentTeacherSearch } from './IntelligentTeacherSearch';
import { MediaUploadAnalysis } from './MediaUploadAnalysis';

interface WalkthroughSession {
  id: string;
  admin_id: string;
  observed_teacher_id: string;
  practice_video_id: string;
  classroom_context: {
    subject?: string;
    grade_level?: string;
    lesson_topic?: string;
    student_count?: number;
    observation_focus?: string;
  };
  observed_strengths: string;
  areas_for_growth: string;
  overall_summary: string;
  recommended_courses: string[];
  observation_date: string;
  status: 'draft' | 'completed' | 'shared';
  created_at: string;
  updated_at: string;
}

interface Teacher {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  department?: string;
  position_title?: string;
}

interface Course {
  id: string;
  title: string;
  description: string;
  category_id?: string;
}

export default function AdminWalkthroughAssistant() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<string>('');
  const [selectedVideo, setSelectedVideo] = useState<string>('');
  const [teacherVideos, setTeacherVideos] = useState<any[]>([]);
  const [currentSession, setCurrentSession] = useState<WalkthroughSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('select');

  // Form state
  const [classroomContext, setClassroomContext] = useState({
    subject: '',
    grade_level: '',
    lesson_topic: '',
    student_count: '',
    observation_focus: ''
  });
  const [observedStrengths, setObservedStrengths] = useState('');
  const [areasForGrowth, setAreasForGrowth] = useState('');
  const [overallSummary, setOverallSummary] = useState('');
  const [recommendedCourses, setRecommendedCourses] = useState<string[]>([]);

  useEffect(() => {
    loadInitialData();
    
    // Check if there's a video ID in URL params
    const videoId = searchParams.get('video');
    const teacherId = searchParams.get('teacher');
    if (videoId && teacherId) {
      setSelectedVideo(videoId);
      setSelectedTeacher(teacherId);
      setActiveTab('observe');
      loadExistingSession(videoId);
    }
  }, [searchParams]);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      
      // Load teachers in district
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: adminProfile } = await supabase
        .from('profiles')
        .select('district_id')
        .eq('id', user.id)
        .single();

      if (!adminProfile) return;

      const [teachersResult, coursesResult] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, first_name, last_name, email, department, position_title')
          .eq('district_id', adminProfile.district_id)
          .neq('is_admin', true)
          .order('last_name'),
        supabase
          .from('courses')
          .select('id, title, description, category_id')
          .eq('status', 'published')
          .order('title')
      ]);

      if (teachersResult.data) setTeachers(teachersResult.data);
      if (coursesResult.data) setCourses(coursesResult.data);

    } catch (error) {
      console.error('Error loading initial data:', error);
      toast({
        title: "Error loading data",
        description: "Please try again later",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadTeacherVideos = async (teacherId: string) => {
    try {
      const { data, error } = await supabase
        .from('practice_videos')
        .select('id, title, description, created_at, status, video_url')
        .eq('user_id', teacherId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTeacherVideos(data || []);
    } catch (error) {
      console.error('Error loading teacher videos:', error);
      toast({
        title: "Error loading videos",
        description: "Could not load teacher's practice videos",
        variant: "destructive"
      });
    }
  };

  const loadExistingSession = async (videoId: string) => {
    try {
      const { data, error } = await supabase
        .from('walkthrough_sessions')
        .select('*')
        .eq('practice_video_id', videoId)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        setCurrentSession(data as WalkthroughSession);
        setClassroomContext((data.classroom_context as any) || {});
        setObservedStrengths((data as any).observed_strengths || '');
        setAreasForGrowth((data as any).areas_for_growth || '');
        setOverallSummary(data.overall_summary || '');
        setRecommendedCourses(data.recommended_courses || []);
      }
    } catch (error) {
      console.error('Error loading existing session:', error);
    }
  };

  const handleTeacherSelect = (teacherId: string) => {
    setSelectedTeacher(teacherId);
    loadTeacherVideos(teacherId);
    setSelectedVideo('');
    setTeacherVideos([]);
  };

  const handleVideoSelect = (videoId: string) => {
    setSelectedVideo(videoId);
    setActiveTab('observe');
    setSearchParams({ teacher: selectedTeacher, video: videoId });
    loadExistingSession(videoId);
  };

  const saveSession = async (status: 'draft' | 'completed' | 'shared' = 'draft') => {
    if (!selectedVideo || !selectedTeacher) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const sessionData = {
        admin_id: user.id,
        observed_teacher_id: selectedTeacher,
        practice_video_id: selectedVideo,
        classroom_context: classroomContext,
        observed_strengths: observedStrengths,
        areas_for_growth: areasForGrowth,
        overall_summary: overallSummary,
        recommended_courses: recommendedCourses,
        status
      };

      if (currentSession) {
        // Update existing session
        const { data, error } = await supabase
          .from('walkthrough_sessions')
          .update(sessionData)
          .eq('id', currentSession.id)
          .select()
          .single();

        if (error) throw error;
        setCurrentSession(data as WalkthroughSession);
      } else {
        // Create new session
        const { data, error } = await supabase
          .from('walkthrough_sessions')
          .insert(sessionData)
          .select()
          .single();

        if (error) throw error;
        setCurrentSession(data as WalkthroughSession);
      }

      toast({
        title: status === 'draft' ? "Session saved" : "Session completed",
        description: status === 'shared' ? "Walkthrough shared with teacher" : "Your walkthrough session has been saved"
      });

    } catch (error) {
      console.error('Error saving session:', error);
      toast({
        title: "Error saving session",
        description: "Please try again later",
        variant: "destructive"
      });
    }
  };

  const toggleCourseRecommendation = (courseId: string) => {
    setRecommendedCourses(prev => 
      prev.includes(courseId) 
        ? prev.filter(id => id !== courseId)
        : [...prev, courseId]
    );
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading walkthrough assistant...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Admin Walkthrough Assistant</h1>
          <p className="text-muted-foreground mt-2">
            Conduct classroom walkthroughs and provide coaching recommendations.
          </p>
          <div className="mt-3 p-4 bg-muted/50 rounded-lg border">
            <p className="text-sm font-medium mb-2">Two Ways to Conduct Walkthroughs:</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• <strong>Quick Upload:</strong> Upload photos/videos from a live classroom observation for instant AI analysis</li>
              <li>• <strong>Review Practice Videos:</strong> Analyze videos teachers have previously uploaded to the platform</li>
            </ul>
          </div>
        </div>
        <Badge variant="outline" className="text-sm">
          <Users className="h-4 w-4 mr-1" />
          Admin Tool
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="upload" className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            <div className="flex flex-col items-start">
              <span>Quick Upload</span>
              <span className="text-xs font-normal text-muted-foreground">Live observations</span>
            </div>
          </TabsTrigger>
          <TabsTrigger value="select" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <div className="flex flex-col items-start">
              <span>Select Teacher</span>
              <span className="text-xs font-normal text-muted-foreground">Review existing videos</span>
            </div>
          </TabsTrigger>
          <TabsTrigger value="observe" className="flex items-center gap-2" disabled={!selectedVideo}>
            <Eye className="h-4 w-4" />
            Video Walkthrough
          </TabsTrigger>
          <TabsTrigger value="recommendations" className="flex items-center gap-2" disabled={!selectedVideo}>
            <BookOpen className="h-4 w-4" />
            Recommendations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="space-y-6">
          <MediaUploadAnalysis />
        </TabsContent>

        <TabsContent value="select" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Select Teacher</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Find teachers who have uploaded practice videos for review
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <IntelligentTeacherSearch
                  onSelectTeacher={handleTeacherSelect}
                  selectedTeacherId={selectedTeacher}
                />
              </CardContent>
            </Card>

            {selectedTeacher && (
              <Card>
                <CardHeader>
                  <CardTitle>Teacher's Practice Videos</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Videos this teacher has previously uploaded to the platform
                  </p>
                </CardHeader>
                <CardContent>
                  {teacherVideos.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      No practice videos available for this teacher.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {teacherVideos.map(video => (
                        <div
                          key={video.id}
                          className="p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleVideoSelect(video.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium">{video.title}</h4>
                              <p className="text-sm text-muted-foreground">
                                {new Date(video.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge className={
                              video.status === 'analyzed' ? 'bg-green-100 text-green-800' :
                              video.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {video.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="observe" className="space-y-6">
          {selectedVideo && (
            <>
              <CoachingVideoPlayer videoId={selectedVideo} autoAnalyze={false} />
              
              <Card>
                <CardHeader>
                  <CardTitle>Classroom Context</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="subject">Subject</Label>
                      <Input
                        id="subject"
                        value={classroomContext.subject}
                        onChange={(e) => setClassroomContext(prev => ({ ...prev, subject: e.target.value }))}
                        placeholder="e.g., Mathematics, English Language Arts"
                      />
                    </div>
                    <div>
                      <Label htmlFor="grade_level">Grade Level</Label>
                      <Input
                        id="grade_level"
                        value={classroomContext.grade_level}
                        onChange={(e) => setClassroomContext(prev => ({ ...prev, grade_level: e.target.value }))}
                        placeholder="e.g., 3rd Grade, High School"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lesson_topic">Lesson Topic</Label>
                      <Input
                        id="lesson_topic"
                        value={classroomContext.lesson_topic}
                        onChange={(e) => setClassroomContext(prev => ({ ...prev, lesson_topic: e.target.value }))}
                        placeholder="e.g., Fraction Operations"
                      />
                    </div>
                    <div>
                      <Label htmlFor="student_count">Student Count</Label>
                      <Input
                        id="student_count"
                        type="number"
                        value={classroomContext.student_count}
                        onChange={(e) => setClassroomContext(prev => ({ ...prev, student_count: e.target.value }))}
                        placeholder="e.g., 24"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="observation_focus">Observation Focus</Label>
                    <Textarea
                      id="observation_focus"
                      value={classroomContext.observation_focus}
                      onChange={(e) => setClassroomContext(prev => ({ ...prev, observation_focus: e.target.value }))}
                      placeholder="What specific aspects of teaching are you focusing on during this walkthrough?"
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Walkthrough Notes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="observed_strengths">Observed Strengths</Label>
                    <Textarea
                      id="observed_strengths"
                      value={observedStrengths}
                      onChange={(e) => setObservedStrengths(e.target.value)}
                      placeholder="Document the teacher's strengths and effective practices observed during the walkthrough..."
                      rows={4}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="areas_for_growth">Areas for Growth</Label>
                    <Textarea
                      id="areas_for_growth"
                      value={areasForGrowth}
                      onChange={(e) => setAreasForGrowth(e.target.value)}
                      placeholder="Identify specific areas where the teacher could focus on professional development..."
                      rows={4}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="overall_summary">Overall Summary & Additional Notes</Label>
                    <Textarea
                      id="overall_summary"
                      value={overallSummary}
                      onChange={(e) => setOverallSummary(e.target.value)}
                      placeholder="Provide a comprehensive summary of the walkthrough and any additional observations..."
                      rows={4}
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button onClick={() => saveSession('draft')} variant="outline">
                  <Save className="h-4 w-4 mr-2" />
                  Save Draft
                </Button>
                <Button onClick={() => saveSession('completed')}>
                  <FileText className="h-4 w-4 mr-2" />
                  Complete Walkthrough
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="recommendations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Course Recommendations</CardTitle>
              <p className="text-sm text-muted-foreground">
                Select courses to recommend based on your walkthrough observations.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {courses.map(course => (
                  <div
                    key={course.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      recommendedCourses.includes(course.id)
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:bg-muted/50'
                    }`}
                    onClick={() => toggleCourseRecommendation(course.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium">{course.title}</h4>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {course.description}
                        </p>
                      </div>
                      {recommendedCourses.includes(course.id) && (
                        <Badge className="ml-2">Recommended</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button onClick={() => saveSession('completed')} variant="outline">
              <Save className="h-4 w-4 mr-2" />
              Save Recommendations
            </Button>
            <Button onClick={() => saveSession('shared')}>
              <Send className="h-4 w-4 mr-2" />
              Share with Teacher
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}